import { type FlightJourneySegment, TGV_STATION_IATA_CODES } from '@massilia/queries'
import { useMemo } from 'react'

import { formatDuration } from '@/utils/format-duration'
import { formatTime } from '@/utils/format-time'

import { StopsIndicator } from './stops-indicator'

// Segment type for different trip modes:
// - 'outbound': outbound segment for one-way or round-trip
// - 'return': return segment for round-trip
// - number: leg index for multi-destination (1, 2, 3, etc.)
export type SegmentType = 'outbound' | 'return' | number

interface FlightCardSegmentProps {
  segment: FlightJourneySegment
  segmentType?: SegmentType
}

export function FlightCardSegment({ segment, segmentType = 'outbound' }: FlightCardSegmentProps) {
  // Helper to get segment label for multi-destination
  const getSegmentLabel = (): null | string => {
    if (typeof segmentType === 'number') {
      return `Segment ${segmentType}`
    }
    return null // No label for simple outbound/return in current design
  }

  const [departureIsTgv, arrivalIsTgv] = useMemo(() => {
    return [
      TGV_STATION_IATA_CODES.has(segment.departureAirport),
      TGV_STATION_IATA_CODES.has(segment.arrivalAirport),
    ]
  }, [segment])

  const segmentLabel = getSegmentLabel()

  // Check if arrival is on a different day than departure
  const isNextDay = segment.arrivalDate !== segment.departureDate

  return (
    <div className="flex flex-col gap-2">
      {/* Segment label for multi-destination */}
      {segmentLabel && (
        <div className="text-xs font-medium tracking-wide text-gray-500 uppercase">
          {segmentLabel}
        </div>
      )}

      <div className="grid grid-cols-[1fr_3fr_1fr] items-center gap-2">
        {/* Departure */}
        <div className="items-left flex flex-col">
          <div className="text-sm font-bold text-gray-900 md:text-base">
            {formatTime(segment.departureTime)}
          </div>
          <div className="text-xs text-gray-600">
            {segment.departureCity} ({segment.departureAirport})
            {departureIsTgv && (
              <img alt="TGV+Air" className="mt-1 h-5 w-auto" src="/logos/logo-tgv-air.png" />
            )}
          </div>
        </div>

        {/* Flight path: separator - indicator - separator */}
        <div className="flex flex-col items-center">
          <span className="mb-0.5 text-[10px] text-gray-500 md:mb-1 md:text-xs">
            {formatDuration(segment.totalDuration)}
          </span>
          <div className="flex w-full items-center">
            <div className="h-0.5 flex-1 bg-gray-300 opacity-0 md:opacity-100" />
            <div className="mx-1 md:mx-3">
              <StopsIndicator legs={segment.legs} stops={segment.stops} variant="badge" />
            </div>
            <div className="h-0.5 flex-1 bg-gray-300 opacity-0 md:opacity-100" />
          </div>
        </div>

        {/* Arrival */}
        <div className="items-right flex w-full flex-col">
          <span className="self-end text-end text-sm font-bold text-gray-900 md:text-base">
            {formatTime(segment.arrivalTime)}
            {isNextDay && <span className="ml-1 text-xs font-normal text-orange-600">+1</span>}
          </span>
          <span className="self-end text-end text-xs text-gray-600">
            {segment.arrivalCity} ({segment.arrivalAirport})
            {arrivalIsTgv && (
              <img alt="TGV+Air" className="mt-1 h-5 w-auto" src="/logos/logo-tgv-air.png" />
            )}
          </span>
        </div>
      </div>
    </div>
  )
}
